import React from 'react';
import LabelTextInput from 'src/modules/shared/components/atoms/inputs/label-text-input';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { HelpTooltip } from 'src/modules/automations/components/atoms/help-tooltip';
import { PiPlus, PiTrash, PiWarningCircle } from 'react-icons/pi';
import { Text } from 'src/modules/shared/components/atoms/text';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import {
  EMPLOYEE_CONSTRAINT_WARNING_CHARACTERS,
  EMPLOYEE_CONSTRAINT_WARNING_COUNT,
} from 'src/modules/automations/constants/employee-constraint.constants';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

interface ConstraintsFieldProps {
  label: string;
  value: string;
  constraints: string[];
  tooltip?: string;
  setNewConstraint: (value: string) => void;
  addConstraint: (value: string) => void;
  removeConstraint: (index: number) => void;
}

export const ConstraintsField: React.FC<ConstraintsFieldProps> = ({
  label,
  value,
  constraints,
  tooltip,
  setNewConstraint,
  addConstraint,
  removeConstraint,
}) => {
  const { language, t } = useI18n();
  const constraintCharacterCount = constraints.reduce(
    (total, constraint) => total + constraint.length,
    0
  );
  const shouldShowSizeWarning =
    constraints.length >= EMPLOYEE_CONSTRAINT_WARNING_COUNT ||
    constraintCharacterCount >= EMPLOYEE_CONSTRAINT_WARNING_CHARACTERS;
  const numberFormatter = new Intl.NumberFormat(language);
  const warningDescriptionKey =
    constraints.length === 1
      ? 'automations.aiAgents.form.constraints.sizeWarningDescriptionSingular'
      : 'automations.aiAgents.form.constraints.sizeWarningDescriptionPlural';

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <div className="flex-grow">
          <LabelTextInput
            label={label}
            labelPosition="top"
            inputProps={{
              value,
              onChange: (e) => setNewConstraint(e.target.value),
              placeholder: t(
                'automations.aiAgents.form.constraints.placeholder'
              ),
              onKeyDown: (e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addConstraint(value);
                }
              },
            }}
          />
        </div>
        <Button
          onClick={() => addConstraint(value)}
          variant="primary"
          appearance="filled"
          size="md"
          type="button"
        >
          <PiPlus className="w-4 h-4" />
          <Text size="small">
            {t('automations.aiAgents.form.constraints.add')}
          </Text>
        </Button>
        {tooltip && <HelpTooltip content={tooltip} />}
      </div>

      {shouldShowSizeWarning ? (
        <div role="status" aria-live="polite">
          <Alert
            variant="warning"
            appearance="soft"
            icon={<PiWarningCircle className="h-4 w-4" />}
            message={
              <div className="space-y-1">
                <Text size="small" weight="semibold">
                  {t('automations.aiAgents.form.constraints.sizeWarningTitle')}
                </Text>
                <Text size="tiny" className="text-warning-800">
                  {t(warningDescriptionKey, {
                    constraintCount: numberFormatter.format(constraints.length),
                    characterCount: numberFormatter.format(
                      constraintCharacterCount
                    ),
                  })}
                </Text>
              </div>
            }
          />
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        {constraints.length === 0 ? (
          <div className="flex items-center justify-between bg-gray-50 p-2 rounded">
            <Text size="small" color="neutral">
              {t('automations.aiAgents.form.constraints.empty')}
            </Text>
          </div>
        ) : (
          constraints.map((item, index) => (
            <div
              key={index}
              className="flex items-center justify-between bg-secondary-50 p-2.5 rounded"
            >
              <Text size="small">{item}</Text>
              <Button
                onClick={() => removeConstraint(index)}
                variant="danger"
                appearance="ghost"
                size="sm"
                isIconOnly
                type="button"
              >
                <PiTrash className="h-4 w-4" />
              </Button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
